/**
 * CapabilityInnovation Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive page displaying Irish Funds capabilities and innovation radar with:
 * - Dynamic capabilities section with vertical layout
 * - Innovation radar visualization with interactive features
 * - Tooltip integration for enhanced information display
 * - HTML content parsing for rich narrative content
 * - Page content management with dynamic loading
 * - Responsive design with gradient backgrounds
 * - Loading states for smooth user experience
 * - Section dividers for clear content separation
 * 
 * Features:
 * - Capabilities overview with detailed information tooltips
 * - Innovation radar with advanced visualization
 * - Dynamic narrative content sections
 * - Interactive UI elements with hover states
 * - Cross-browser compatibility
 * 
 * @returns {JSX.Element} CapabilityInnovation page component
 */
import React, { useEffect, useState } from 'react';
import Divider from '../components/Common/Divider';
import Capabilities from '../components/CapabilitiyInnovation/CapabilitiesVertical';
import apiHelper from '../utils/apiHelper';
import InnovationRadar from '../components/CapabilitiyInnovation/InnovationRadar';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import parse from 'html-react-parser';
import './CapabilityInnovation.css';

const { FILE_NAMES } = require('../utils/constants');

/**
 * CapabilityInnovation component for displaying capabilities and innovation
 * radar with dynamic content and interactive features
 */
function CapabilityInnovation() {
  const [capabilitiesData, setCapabilitiesData] = useState([]);
  const [innovationData, setInnovationData] = useState([]);
  const [capabilitiesNarrative, setCapabilitiesNarrative] = useState('');
  const [innovationNarrative, setInnovationNarrative] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const capabilitiesDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.CAPABILITIES);
        const innovationDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.INNOVATION);
        const pageContentResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.PAGECONTENT);
        
        // Narrative content
        const capabilitiesNarrativeContent = pageContentResult.fileContent.find(item => item.id === 9);
        const innovationNarrativeContent = pageContentResult.fileContent.find(item => item.id === 10);
        
        setCapabilitiesNarrative(capabilitiesNarrativeContent?.htmlContent ? String(capabilitiesNarrativeContent.htmlContent) : '');
        setInnovationNarrative(innovationNarrativeContent?.htmlContent ? String(innovationNarrativeContent.htmlContent) : '');
        
        setCapabilitiesData(capabilitiesDataResult.fileContent);
        setInnovationData(innovationDataResult.fileContent);
      } catch (err) {
        console.error('Error fetching capability innovation data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <div className='totalPage'>
      <section className="wrapper">
          <div id="capabilities" className="container mt-6">
          <h2 className="text-uppercase main-header">Capabilities</h2>
          
          {capabilitiesNarrative && (
            <div className="narrative-content mt-4 mb-4">
              {parse(capabilitiesNarrative)}
            </div>
          )}
          
          <div className='mt-6'>
            {capabilitiesData.length > 0 ? (
              <Capabilities data={capabilitiesData} />
            ) : (
              <LoadingSpinner text="Loading Capabilities" />
            )}
          </div>
        </div>
      </section>

      <Divider height={60} />

      <section className="wrapper bg-gradient-primary">
        <div id="innovation" className="container pb-6">
          <h2 className="text-uppercase main-header">Innovation Radar</h2>
          
          {innovationNarrative && (
            <div className="narrative-content mt-4 mb-4">
              {parse(innovationNarrative)}
            </div>
          )}
          
          <div className='mt-6'>
            {innovationData.length > 0 ? (
              <InnovationRadar data={innovationData} />
            ) : (
              <LoadingSpinner text="Loading Innovation Radar" />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CapabilityInnovation;